import React from 'react';
import { Helmet } from 'react-helmet';
import { SITE, metaFor, normalizePath } from '../../data/seo';

/**
 * Seo — one place that sets title, description, canonical, Open Graph and Twitter tags
 * for every public route (see data/seo-routes.json).
 *
 * - /blog/:slug sets its own tags in BlogArticle, so nothing is emitted here.
 * - Unknown URLs (404) are marked noindex.
 */
function Seo({ pathname }) {
  const path = normalizePath(pathname);
  if (path.startsWith('/blog/')) return null;

  const meta = metaFor(path);

  if (!meta) {
    return (
      <Helmet>
        <title>Page Not Found | Quantum Stack</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
    );
  }

  const url = `${SITE.url}${path === '/' ? '/' : path}`;
  const segments = path.split('/').filter(Boolean);
  const showCrumbs = segments.length > 0 && !path.startsWith('/solutions');

  const crumbs = showCrumbs
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE.url}/` },
          ...segments.map((seg, i) => ({
            '@type': 'ListItem',
            position: i + 2,
            name: seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
            item: `${SITE.url}/${segments.slice(0, i + 1).join('/')}`,
          })),
        ],
      }
    : null;

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={url} />
      {meta.noindex && <meta name="robots" content="noindex, follow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content={SITE.locale} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={SITE.image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={SITE.image} />

      {crumbs && <script type="application/ld+json">{JSON.stringify(crumbs)}</script>}
    </Helmet>
  );
}

export default Seo;
